import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import GitHubIcon from '@material-ui/icons/GitHub';
import SettingsIcon from '@material-ui/icons/SettingsOutlined';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { colors } from '../theme';
import { inputStyle, labelStyle, ProgressStepper } from '../formPrimitives';
import { EnvVarsEditor, type EnvVarRow } from './EnvVarsEditor';
import { DepartmentSelect } from './DepartmentSelect';
import { CreateKindGrid } from './CreateKindGrid';
import { useIdentity } from '../useIdentity';
import { sanitizeName } from '../nameUtils';
import { checkAppNameAvailability, deploySmartApp, getWorkflowStatus } from '../api';
import { pickFromDrop, pickFromInput, suggestAppName, toRequestSource, type PickedSource } from '../uploadUtils';

type Phase =
  | { phase: 'idle' }
  | { phase: 'picked'; source: PickedSource }
  | { phase: 'github' }
  | { phase: 'working'; stage: number }
  | { phase: 'done'; url?: string; stack?: string; components?: DoneComponent[]; services?: string[]; files?: string[]; repairs?: string[] }
  | { phase: 'failed'; message: string };

interface DoneComponent {
  name: string;
  role: 'frontend' | 'backend';
  dir: string;
  stack: string;
  url?: string;
}

const SERVICE_LABEL: Record<string, string> = { postgres: 'PostgreSQL', mysql: 'MySQL', redis: 'Redis', mongo: 'MongoDB' };

const STAGES = ['Uploading your project', 'Reading your project', 'Getting your app ready', 'Going live'] as const;

/** Workflow status -> index into STAGES. */
function stageFor(status: string): number {
  if (status === 'READING_PROJECT') return 1;
  if (status.startsWith('BUILDING_IMAGE') || status.startsWith('CREATING_DATABASE') || status.startsWith('WRITING_DOCKER_FILES')) return 2;
  return 3;
}

const primaryBtn: CSSProperties = {
  background: colors.primary,
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '12px 32px',
  fontSize: 15,
  fontWeight: 600,
  cursor: 'pointer',
};

const linkBtn: CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: colors.primary,
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 600,
  padding: 0,
};

function friendlyError(message: string): string {
  if (/already exists|name is taken|409/i.test(message)) {
    return 'An app with that name already exists. Open Advanced settings and pick another name.';
  }
  return message.split('\n')[0] || 'Something went wrong.';
}

/**
 * The one entry point for non-technical users: drop a folder or ZIP (or paste
 * a GitHub repo), and the platform works out the tech stack, builds and
 * deploys it. There is deliberately no runtime / port / template choice here —
 * that is the server's own detection (SmartDeployWorkflow).
 */
export function DeployDropzone({ onDeployed }: { onDeployed?: () => void }) {
  const identityApi = useApi(identityApiRef);
  const { email } = useIdentity();
  const [state, setState] = useState<Phase>({ phase: 'idle' });
  const [dragging, setDragging] = useState(false);
  const [name, setName] = useState('');
  const [nameTaken, setNameTaken] = useState(false);
  const [repo, setRepo] = useState('');
  const [advanced, setAdvanced] = useState(false);
  const [department, setDepartment] = useState('general');
  const [environment, setEnvironment] = useState('development');
  const [envRows, setEnvRows] = useState<EnvVarRow[]>([]);
  const [database, setDatabase] = useState<'' | 'postgres' | 'mysql'>('');
  const [error, setError] = useState<string | null>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  const zipInput = useRef<HTMLInputElement>(null);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  // webkitdirectory is not in React's input typings.
  useEffect(() => {
    folderInput.current?.setAttribute('webkitdirectory', '');
  }, []);

  // Live "is this name taken" check — same GitHub-backed source as the other
  // create flows.
  useEffect(() => {
    if (!name || (state.phase !== 'picked' && state.phase !== 'github')) return undefined;
    setNameTaken(false);
    const t = setTimeout(() => {
      checkAppNameAvailability(identityApi, name, department)
        .then((r) => alive.current && setNameTaken(!r.available))
        .catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [name, department, identityApi, state.phase]);

  const accept = (src: PickedSource) => {
    setError(null);
    setName(suggestAppName(src.label));
    setState({ phase: 'picked', source: src });
  };

  const guard = async (fn: () => PickedSource | Promise<PickedSource>) => {
    try {
      accept(await fn());
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const envVars = Object.fromEntries(envRows.filter((r) => r.key.trim()).map((r) => [r.key.trim(), r.value]));
  const repoSlug = (r: string) => sanitizeName(r.trim().replace(/\.git$/, '').replace(/\/$/, '').split('/').pop() ?? '');

  const start = async () => {
    const source = state.phase === 'picked' ? state.source : null;
    if (!source && state.phase !== 'github') return;
    setError(null);
    setState({ phase: 'working', stage: 0 });
    try {
      const reqSource = source ? await toRequestSource(source) : { type: 'github' as const, repo: repo.trim() };
      const { workflowId } = await deploySmartApp(identityApi, email, {
        name,
        department,
        environment,
        envVars: Object.keys(envVars).length ? envVars : undefined,
        database: database || undefined,
        source: reqSource,
      });
      // Builds can take a while: 5s polls, 15 min ceiling.
      for (let i = 0; i < 180; i++) {
        if (!alive.current) return;
        const res = await getWorkflowStatus(identityApi, workflowId);
        if (res.result) {
          const r = res.result as {
            status?: string;
            liveUrl?: string;
            stack?: string;
            failureMessage?: string;
            components?: DoneComponent[];
            provisionedServices?: string[];
            generatedFiles?: string[];
            repairs?: string[];
          };
          if (r.status === 'DEPLOYED') {
            setState({
              phase: 'done',
              url: r.liveUrl,
              stack: r.stack,
              components: r.components,
              services: r.provisionedServices,
              files: r.generatedFiles,
              repairs: r.repairs,
            });
            onDeployed?.();
            return;
          }
          throw new Error(r.failureMessage ?? 'Deployment failed');
        }
        setState({ phase: 'working', stage: stageFor(res.status) });
        await new Promise((r) => setTimeout(r, 5000));
      }
      throw new Error('This is taking longer than expected. Check "Your apps" in a few minutes.');
    } catch (e) {
      if (alive.current) setState({ phase: 'failed', message: (e as Error).message });
    }
  };

  const reset = () => {
    setState({ phase: 'idle' });
    setError(null);
    setName('');
    setRepo('');
  };

  const card: CSSProperties = {
    border: `2px dashed ${dragging ? colors.primary : '#c9ccf5'}`,
    background: dragging ? '#f1f2ff' : '#f8f8ff',
    borderRadius: 16,
    padding: '32px 32px 20px',
    transition: 'background 120ms, border-color 120ms',
  };

  const advancedPanel = (
    <div style={{ borderTop: `1px solid ${colors.border}`, marginTop: 24, paddingTop: 12, textAlign: 'left' }}>
      <button
        type="button"
        onClick={() => setAdvanced((a) => !a)}
        aria-expanded={advanced}
        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '6px 0', fontSize: 14, color: colors.text }}
      >
        <SettingsIcon style={{ fontSize: 18, color: colors.textMuted }} />
        <span>Advanced settings</span>
        <ExpandMoreIcon style={{ marginLeft: 'auto', color: colors.textMuted, transform: advanced ? 'rotate(180deg)' : 'none' }} />
      </button>
      {advanced && (
        <div style={{ paddingTop: 16 }}>
          {/* Both columns: one label, then a select — so the two controls line up. */}
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <DepartmentSelect label="Team" value={department} onChange={setDepartment} />
            </div>
            <div style={{ flex: 1, marginBottom: 20 }}>
              <label style={labelStyle} htmlFor="launchpad-environment">
                Where should it run?
              </label>
              <select
                id="launchpad-environment"
                style={inputStyle}
                value={environment}
                onChange={(e) => setEnvironment(e.target.value)}
              >
                <option value="development">Testing</option>
                <option value="staging">Staging</option>
              </select>
            </div>
          </div>
          <span style={labelStyle}>Settings your app needs (optional)</span>
          <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 8 }}>
            API keys and other values your app reads when it starts.
          </div>
          <EnvVarsEditor rows={envRows} onChange={setEnvRows} />
          <div style={{ marginTop: 24 }}>
            <span style={labelStyle}>Other ways to create</span>
            <CreateKindGrid />
          </div>
        </div>
      )}
    </div>
  );

  if (state.phase === 'working') {
    return (
      <div style={{ ...card, border: `2px solid ${colors.border}`, background: colors.surface }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 20, color: colors.text }}>Putting your app online…</h2>
        <p style={{ margin: '0 0 16px', color: colors.textMuted, fontSize: 14 }}>
          This usually takes a few minutes. Keep this page open to see when it&apos;s ready.
        </p>
        <ProgressStepper stages={STAGES} currentStage={state.stage} />
      </div>
    );
  }

  if (state.phase === 'done') {
    return (
      <div style={{ ...card, border: '2px solid #bbf7d0', background: '#f0fdf4', textAlign: 'center' }}>
        <CheckCircleIcon style={{ fontSize: 48, color: colors.success }} />
        <h2 style={{ margin: '8px 0 4px', fontSize: 22, color: colors.text }}>Your app is online</h2>
        {state.stack && (
          <p style={{ margin: '0 0 12px', color: colors.textMuted, fontSize: 14 }}>
            We detected: <strong>{state.stack}</strong>
          </p>
        )}
        {state.components && state.components.length > 1 ? (
          <div style={{ margin: '0 0 16px', textAlign: 'left', display: 'inline-block' }}>
            {state.components.map((c) => (
              <div key={c.name} style={{ fontSize: 14, margin: '4px 0' }}>
                <strong>{c.role === 'frontend' ? 'Frontend' : 'Backend'}</strong> <span style={{ color: colors.textMuted }}>({c.dir}, {c.stack})</span>{' '}
                {c.url && (
                  <a href={c.url} target="_blank" rel="noreferrer" style={{ color: colors.primary, fontWeight: 600 }}>
                    {c.url.replace(/^https?:\/\//, '')}
                  </a>
                )}
              </div>
            ))}
          </div>
        ) : (
          state.url && (
            <p style={{ margin: '0 0 16px' }}>
              <a href={state.url} target="_blank" rel="noreferrer" style={{ color: colors.primary, fontSize: 16, fontWeight: 600 }}>
                {state.url.replace(/^https?:\/\//, '')}
              </a>
            </p>
          )
        )}
        {state.services && state.services.length > 0 && (
          <p style={{ margin: '0 0 12px', color: colors.textMuted, fontSize: 13 }}>
            We also set up {state.services.map((x) => SERVICE_LABEL[x] ?? x).join(', ')} and added the connection to your app&apos;s
            environment variables.
          </p>
        )}
        {state.repairs && state.repairs.length > 0 && (
          <p style={{ margin: '0 0 12px', color: colors.textMuted, fontSize: 13 }}>
            We fixed a problem in your project so it could run: {state.repairs.join(' ')}
          </p>
        )}
        {state.files && state.files.length > 0 && (
          <p style={{ margin: '0 0 12px', color: colors.textMuted, fontSize: 13 }}>
            Your project had no Docker files, so we created <code>{state.files.join(', ')}</code> in its repository.
          </p>
        )}
        {state.url && (
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button type="button" style={primaryBtn} onClick={() => window.open(state.url, '_blank')}>Open app</button>
            <button
              type="button"
              style={{ ...primaryBtn, background: colors.surface, color: colors.text, border: `1px solid ${colors.border}` }}
              onClick={() => navigator.clipboard?.writeText(state.url ?? '')}
            >
              Copy link
            </button>
          </div>
        )}
        <div style={{ marginTop: 16 }}>
          <button type="button" style={linkBtn} onClick={reset}>Put another app online</button>
        </div>
      </div>
    );
  }

  if (state.phase === 'failed') {
    return (
      <div style={{ ...card, border: '2px solid #fecaca', background: '#fef2f2' }}>
        <h2 style={{ margin: '0 0 6px', fontSize: 20, color: colors.text }}>We couldn&apos;t put your app online</h2>
        <p style={{ margin: '0 0 12px', color: colors.text, fontSize: 14 }}>{friendlyError(state.message)}</p>
        <details style={{ marginBottom: 16, fontSize: 12, color: colors.textMuted }}>
          <summary style={{ cursor: 'pointer' }}>Technical details (for the platform team)</summary>
          <pre style={{ whiteSpace: 'pre-wrap', margin: '8px 0 0' }}>{state.message}</pre>
        </details>
        <button type="button" style={primaryBtn} onClick={reset}>Try again</button>
      </div>
    );
  }

  if (state.phase === 'picked' || state.phase === 'github') {
    const picked = state.phase === 'picked' ? state.source : null;
    const detail = picked?.kind === 'files' ? `${picked.files.length} files` : picked ? 'ZIP file' : '';
    const canGo = !nameTaken && name.length > 0 && (picked ? true : /^[\w.-]+\/[\w.-]+|github\.com\//.test(repo.trim()));
    return (
      <div style={{ ...card, border: `2px solid ${colors.border}`, background: colors.surface }}>
        {picked ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <FolderOpenIcon style={{ fontSize: 36, color: colors.primary }} />
            <div style={{ flex: 1, textAlign: 'left' }}>
              <div style={{ fontWeight: 600, color: colors.text }}>{picked.label}</div>
              <div style={{ fontSize: 13, color: colors.textMuted }}>{detail}</div>
            </div>
            <button type="button" style={linkBtn} onClick={reset}>Change</button>
          </div>
        ) : (
          <div style={{ textAlign: 'left', marginBottom: 20 }}>
            <label style={labelStyle}>
              GitHub repository
              <input
                style={inputStyle}
                placeholder="mumzworld-tech/my-project, or paste its link"
                value={repo}
                onChange={(e) => {
                  setRepo(e.target.value);
                  setName(suggestAppName(repoSlug(e.target.value)));
                }}
              />
            </label>
            <button type="button" style={linkBtn} onClick={reset}>Back</button>
          </div>
        )}
        <div style={{ textAlign: 'left', marginBottom: 20 }}>
          <label style={labelStyle}>
            Name your app
            <input style={inputStyle} value={name} onChange={(e) => setName(sanitizeName(e.target.value))} />
          </label>
          <div style={{ fontSize: 12, color: nameTaken ? colors.danger : colors.textMuted }}>
            {nameTaken ? 'That name is already in use. Try another.' : name && `Your link will be ${name}.coolify.mumzstage.com`}
          </div>
          <label style={{ ...labelStyle, display: 'block', marginTop: 16 }}>
            Does your app need a database?
            <select
              style={inputStyle}
              value={database}
              onChange={(e) => setDatabase(e.target.value as '' | 'postgres' | 'mysql')}
            >
              <option value="">No database</option>
              <option value="postgres">Yes — PostgreSQL</option>
              <option value="mysql">Yes — MySQL</option>
            </select>
          </label>
          {database && (
            <div style={{ fontSize: 12, color: colors.textMuted }}>
              We create a private database for your app and give it the connection as <code>DATABASE_URL</code>.
            </div>
          )}
        </div>
        {error && <div role="alert" style={{ color: colors.danger, fontSize: 14, marginBottom: 12 }}>{error}</div>}
        <div style={{ textAlign: 'center' }}>
          <button
            type="button"
            style={{ ...primaryBtn, opacity: canGo ? 1 : 0.5, cursor: canGo ? 'pointer' : 'not-allowed' }}
            disabled={!canGo}
            onClick={() => void start()}
          >
            Put my app online
          </button>
        </div>
        {advancedPanel}
      </div>
    );
  }

  return (
    <div
      style={{ ...card, textAlign: 'center' }}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); void guard(() => pickFromDrop(e.dataTransfer)); }}
    >
      <CloudUploadIcon style={{ fontSize: 72, color: '#a5a8f5' }} />
      <h2 style={{ margin: '8px 0 4px', fontSize: 20, fontWeight: 600, color: colors.text }}>Drop your app folder or ZIP here</h2>
      <p style={{ margin: '0 0 20px', color: colors.textMuted, fontSize: 14 }}>You can upload a folder or a .zip file.</p>
      {error && <div role="alert" style={{ color: colors.danger, fontSize: 14, marginBottom: 12 }}>{error}</div>}
      <button type="button" style={primaryBtn} onClick={() => zipInput.current?.click()}>Choose files</button>
      <input ref={zipInput} type="file" accept=".zip" hidden onChange={(e) => e.target.files && void guard(() => pickFromInput(e.target.files!))} />
      <div style={{ marginTop: 10 }}>
        <button type="button" style={{ ...linkBtn, fontWeight: 400, fontSize: 13, color: colors.textMuted }} onClick={() => folderInput.current?.click()}>
          or pick a folder
        </button>
        <input ref={folderInput} type="file" multiple hidden onChange={(e) => e.target.files && void guard(() => pickFromInput(e.target.files!))} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '18px auto', maxWidth: 260, color: colors.textMuted, fontSize: 13 }}>
        <span style={{ flex: 1, height: 1, background: colors.border }} />or<span style={{ flex: 1, height: 1, background: colors.border }} />
      </div>
      <button
        type="button"
        style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 12, textAlign: 'left' }}
        onClick={() => { setError(null); setState({ phase: 'github' }); }}
      >
        <GitHubIcon style={{ fontSize: 30, color: colors.text }} />
        <span>
          <span style={{ display: 'block', color: colors.primary, fontWeight: 600, fontSize: 14 }}>Import from GitHub</span>
          <span style={{ display: 'block', color: colors.textMuted, fontSize: 12 }}>Connect your repository</span>
        </span>
      </button>
      {advancedPanel}
      <div style={{ marginTop: 8, fontSize: 12, color: colors.textMuted }}>
        Stuck? <Link to="/help" style={{ color: colors.primary }}>Ask the platform team</Link>
      </div>
    </div>
  );
}
